'use client';

/**
 * PlaceMenuList — the menu, as a menu.
 *
 * WHY MENURAQ OWNS THIS. Templates style the menu body; they do not get to
 * decide whether the menu is legible or orderable. A paged-flip template turned
 * a 27-item menu into "Page 1 of 21 · Swipe to turn", with the dishes themselves
 * behind nineteen page turns and no add-to-order control anywhere on screen.
 * That is not a styling choice, it is the product failing to do its job.
 *
 * So a paged template now renders through here: it keeps everything that makes
 * it the merchant's — palette, display face, card colour, accent — and loses the
 * pager. The merchant's identity survives; the guest's ability to order survives
 * with it.
 *
 * THE ROW IS THE PRODUCT. A guest scanning a menu on a phone is doing one thing:
 * matching a dish to a price and deciding. So each row carries exactly that —
 * picture, name, the sentence that sells it, the price, and the way to add it.
 * Anything else competes with the decision.
 *
 * THE ACCENT BUDGET. The add control is the only accent-coloured element on the
 * screen. Section headings, prices and tags are all text; if everything is
 * emphasised, the guest has nothing to aim at.
 *
 * THREE LAYOUTS, chosen by the menu's SHAPE rather than by taste:
 *
 *   rows      ┌──────┐ Name              A dense scan. The only layout that
 *             │ img  │ Description       stays legible past ~30 items, so it
 *             └──────┘ ₵00.00      (+)   is the default.
 *
 *   cards     ┌────────┐ ┌────────┐      Two columns, photo first. For shorter
 *             │  img   │ │  img   │      photogenic menus where the picture is
 *             │ Name   │ │ Name   │      doing the selling. Descriptions are
 *             │ ₵00 (+)│ │ ₵00 (+)│      clipped hard — a card is a glance.
 *             └────────┘ └────────┘
 *
 *   showcase  ┌──────────────────────┐   One dish per row, full width. For a
 *             │        image         │   short menu where each dish is the
 *             │ Name          ₵00.00 │   reason someone came. Expensive in
 *             │ Description     (+)  │   vertical space, so it is wrong for a
 *             └──────────────────────┘   long list and right for eight dishes.
 */

import Image from 'next/image';
import { Plus } from 'lucide-react';
import type { CSSProperties } from 'react';

import { cn } from '@/components/ui/utils';
import type { OrderingMenuItem } from '@/lib/menu/ordering/types';

export type PlaceMenuSection = {
  id: string;
  name: string;
  description?: string | null;
  items: OrderingMenuItem[];
};

export type PlaceMenuLayout = 'rows' | 'cards' | 'showcase';

export function PlaceMenuList({
  sections,
  onOpenItem,
  onAddItem,
  /** The merchant's display face, for section headings and dish names. */
  headingFont,
  layout = 'rows',
  emptyMessage = 'Nothing on the menu right now.',
  className,
  /**
   * When the menu content was last saved (the menu's `updated_at`). Renders the
   * doc 34 §3.1 freshness label — "Showing saved menu from …" — so a guest can
   * tell a snapshot menu from a live one. Omitted when the caller has no
   * timestamp (all current callers); the label is a deliberate, honest extra,
   * never a default.
   */
  lastUpdatedAt,
}: {
  sections: PlaceMenuSection[];
  onOpenItem: (item: OrderingMenuItem) => void;
  /** Omitted when the branch cannot take an order — the row then has no add control. */
  onAddItem?: (item: OrderingMenuItem) => void;
  headingFont?: string;
  layout?: PlaceMenuLayout;
  emptyMessage?: string;
  className?: string;
  lastUpdatedAt?: string | null;
}) {
  const populated = sections.filter((section) => section.items.length > 0);

  if (populated.length === 0) {
    return (
      <p className="px-5 py-16 text-center text-sm text-[var(--color-text-secondary)]">
        {emptyMessage}
      </p>
    );
  }

  const headingStyle: CSSProperties | undefined = headingFont
    ? { fontFamily: headingFont }
    : undefined;

  // Fixed locale + explicit fields so the server and client render the same
  // string (a hydration mismatch would flash a different time).
  const freshnessLabel = lastUpdatedAt
    ? `Showing saved menu from ${new Date(lastUpdatedAt).toLocaleString('en-GB', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })}`
    : null;

  return (
    // The measure lives here rather than on the shell's <main>, so the chrome
    // bars above stay full-bleed while the dishes sit on a readable column.
    <div className={cn('mx-auto w-full [max-width:var(--place-measure,100%)]', className)}>
      {freshnessLabel ? (
        <p className="px-5 pt-4 text-xs text-[var(--color-text-secondary)]">{freshnessLabel}</p>
      ) : null}
      {populated.map((section) => (
        <section key={section.id} className="pt-6">
          <div className="px-5">
            <h2
              className="text-lg font-semibold leading-tight text-[var(--color-text-primary)]"
              style={headingStyle}
            >
              {section.name}
            </h2>
            {section.description ? (
              <p className="pt-0.5 text-sm text-[var(--color-text-secondary)]">
                {section.description}
              </p>
            ) : null}
          </div>

          <ul
            className={cn(
              'pt-2',
              layout === 'cards' && 'grid grid-cols-2 gap-3 px-5 pt-3',
              layout === 'showcase' && 'space-y-4 px-5 pt-3',
            )}
          >
            {section.items.map((item) => {
              const shared = {
                item,
                headingStyle,
                onOpen: () => onOpenItem(item),
                onAdd: onAddItem ? () => onAddItem(item) : undefined,
              };
              if (layout === 'cards') return <PlaceMenuCard key={item.id} {...shared} />;
              if (layout === 'showcase') return <PlaceMenuShowcase key={item.id} {...shared} />;
              return <PlaceMenuRow key={item.id} {...shared} />;
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}

function PlaceMenuRow({
  item,
  headingStyle,
  onOpen,
  onAdd,
}: {
  item: OrderingMenuItem;
  headingStyle?: CSSProperties;
  onOpen: () => void;
  onAdd?: () => void;
}) {
  const name = item.title || item.name || 'Item';
  const price = formatMenuPrice(item);
  // `available_for_order` is the branch-level verdict and outranks the item's own
  // flag: an item can be on the menu and still be off at this branch today.
  const orderable = item.available !== false && item.available_for_order !== false;

  return (
    <li className="border-b border-[var(--color-border-subtle)] last:border-b-0">
      <div className="flex items-start gap-3 px-5 py-3.5">
        {/* The whole text block is the tap target for details — a phone-sized
            row with a separate "view" affordance wastes the 80% of the row that
            is already sitting under the guest's thumb. */}
        <button
          type="button"
          onClick={onOpen}
          className={cn(
            'flex min-w-0 flex-1 items-start gap-3 text-left',
            'outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)]',
            !orderable && 'opacity-55',
          )}
        >
          {item.image ? (
            <span className="relative size-[68px] shrink-0 overflow-hidden rounded-xl bg-[var(--color-bg-sunken)]">
              <Image
                src={item.image}
                alt=""
                fill
                sizes="68px"
                className="object-cover"
                // A dish photo is decoration for the name beside it, so it never
                // blocks first paint of the text the guest is actually reading.
                loading="lazy"
              />
            </span>
          ) : null}

          <span className="min-w-0 flex-1">
            <span
              className="block text-[15px] font-semibold leading-snug text-[var(--color-text-primary)]"
              style={headingStyle}
            >
              {name}
            </span>

            {item.description ? (
              <span className="mt-0.5 line-clamp-2 text-[13px] leading-snug text-[var(--color-text-secondary)]">
                {item.description}
              </span>
            ) : null}

            <span className="mt-1.5 flex items-center gap-2">
              {price ? (
                <span className="font-[family-name:var(--font-place-data)] text-sm tabular-nums text-[var(--color-text-primary)]">
                  {price}
                </span>
              ) : null}
              {!orderable ? (
                <span className="text-xs font-medium text-[var(--color-text-secondary)]">
                  {item.availability_label || 'Not available'}
                </span>
              ) : null}
            </span>
          </span>
        </button>

        {onAdd && orderable ? (
          <button
            type="button"
            onClick={onAdd}
            aria-label={`Add ${name} to your order`}
            // The circle stays 36px because that is what the row is drawn
            // around; the BUTTON is 44 (doc 05 §5.4). This is the control the
            // whole surface exists to offer — it cannot be the thing a thumb
            // misses. `-m-1` absorbs the extra 8px so nothing shifts.
            className={cn(
              '-m-1 inline-flex size-11 shrink-0 items-center justify-center rounded-full',
              'transition-transform active:scale-95',
              'outline-none focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--color-accent)]',
            )}
          >
            <span className="flex size-9 items-center justify-center rounded-full bg-[var(--color-accent)] text-[var(--color-accent-text)]">
              <Plus aria-hidden="true" className="size-5" />
            </span>
          </button>
        ) : null}
      </div>
    </li>
  );
}

/**
 * `cards` — two columns, photo first.
 *
 * The picture leads because on a short photogenic menu it is doing the selling.
 * The description is clipped to one line rather than omitted: a card with no
 * words at all makes two similar dishes indistinguishable, and a card with three
 * lines stops being a glance.
 */
function PlaceMenuCard({
  item,
  headingStyle,
  onOpen,
  onAdd,
}: {
  item: OrderingMenuItem;
  headingStyle?: CSSProperties;
  onOpen: () => void;
  onAdd?: () => void;
}) {
  const name = item.title || item.name || 'Item';
  const price = formatMenuPrice(item);
  const orderable = item.available !== false && item.available_for_order !== false;

  return (
    <li
      className={cn(
        'overflow-hidden rounded-2xl border border-[var(--color-border-subtle)]',
        'bg-[var(--color-bg-surface)]',
        !orderable && 'opacity-55',
      )}
    >
      <button
        type="button"
        onClick={onOpen}
        className={cn(
          'block w-full text-left',
          'outline-none focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--color-accent)]',
        )}
      >
        {/* A fixed 4:3 well, always present. Without a reserved box a menu where
            only some dishes have photos becomes a ragged column of different
            card heights. */}
        <span className="relative block aspect-[4/3] w-full bg-[var(--color-bg-sunken)]">
          {item.image ? (
            <Image src={item.image} alt="" fill sizes="(max-width: 640px) 50vw, 220px" className="object-cover" loading="lazy" />
          ) : null}
        </span>

        <span className="block px-3 pb-2 pt-2.5">
          <span
            className="line-clamp-2 text-[14px] font-semibold leading-snug text-[var(--color-text-primary)]"
            style={headingStyle}
          >
            {name}
          </span>
          {item.description ? (
            <span className="mt-0.5 line-clamp-1 text-[12px] leading-snug text-[var(--color-text-secondary)]">
              {item.description}
            </span>
          ) : null}
        </span>
      </button>

      <div className="flex items-center justify-between gap-2 px-3 pb-3">
        <span className="min-w-0 font-[family-name:var(--font-place-data)] text-sm tabular-nums text-[var(--color-text-primary)]">
          {orderable ? price : item.availability_label || 'Unavailable'}
        </span>
        {onAdd && orderable ? (
          <button
            type="button"
            onClick={onAdd}
            aria-label={`Add ${name} to your order`}
            // Same as the row: a 32px circle inside a 44px button, with the
            // overflow pulled back so the card's footer keeps its height.
            className={cn(
              '-m-1.5 inline-flex size-11 shrink-0 items-center justify-center rounded-full',
              'transition-transform active:scale-95',
              'outline-none focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--color-accent)]',
            )}
          >
            <span className="flex size-8 items-center justify-center rounded-full bg-[var(--color-accent)] text-[var(--color-accent-text)]">
              <Plus aria-hidden="true" className="size-4" />
            </span>
          </button>
        ) : null}
      </div>
    </li>
  );
}

/**
 * `showcase` — one dish per row, full width.
 *
 * The image is the row. Name and price share a baseline beneath it so the eye
 * lands on both at once rather than hunting for the number, and the add control
 * sits on the same line as the price it commits to.
 *
 * Deliberately expensive in vertical space: this is for a menu of eight dishes
 * where each one is the reason someone came, and it is the wrong choice for
 * thirty, which is why `rows` remains the default.
 */
function PlaceMenuShowcase({
  item,
  headingStyle,
  onOpen,
  onAdd,
}: {
  item: OrderingMenuItem;
  headingStyle?: CSSProperties;
  onOpen: () => void;
  onAdd?: () => void;
}) {
  const name = item.title || item.name || 'Item';
  const price = formatMenuPrice(item);
  const orderable = item.available !== false && item.available_for_order !== false;

  return (
    // No `overflow-hidden` here: the image rounds itself, and clipping the row
    // cut the descenders off the last line of every description.
    <li className={cn(!orderable && 'opacity-55')}>
      <button
        type="button"
        onClick={onOpen}
        className={cn(
          'block w-full text-left',
          'outline-none focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--color-accent)]',
        )}
      >
        {item.image ? (
          <span className="relative block aspect-[16/10] w-full overflow-hidden rounded-3xl bg-[var(--color-bg-sunken)]">
            <Image src={item.image} alt="" fill sizes="100vw" className="object-cover" loading="lazy" />
          </span>
        ) : null}

        <span className="mt-2.5 flex items-baseline justify-between gap-3">
          <span
            className="min-w-0 text-[17px] font-semibold leading-snug text-[var(--color-text-primary)]"
            style={headingStyle}
          >
            {name}
          </span>
          <span className="shrink-0 font-[family-name:var(--font-place-data)] text-[15px] tabular-nums text-[var(--color-text-primary)]">
            {orderable ? price : ''}
          </span>
        </span>
      </button>

      <div className="mt-1 flex items-end justify-between gap-3">
        <p className="min-w-0 flex-1 text-[13px] leading-snug text-[var(--color-text-secondary)]">
          {orderable ? item.description : item.availability_label || 'Not available'}
        </p>
        {onAdd && orderable ? (
          <button
            type="button"
            onClick={onAdd}
            aria-label={`Add ${name} to your order`}
            className={cn(
              // 44px floor (doc 05 §5.4); it carries a word, so the height is
              // the whole control rather than a circle inside a hit area.
              'inline-flex h-11 shrink-0 items-center gap-1.5 rounded-full px-4 text-sm font-semibold',
              'bg-[var(--color-accent)] text-[var(--color-accent-text)]',
              'transition-transform active:scale-95',
              'outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)]',
            )}
          >
            <Plus aria-hidden="true" className="size-4" />
            Add
          </button>
        ) : null}
      </div>
    </li>
  );
}

/**
 * Price arrives as a number for plain dishes and as a pre-formatted string for
 * per-unit pricing ("45.00 per person"), with the currency symbol resolved
 * upstream by the template pipeline. Both branches must carry the symbol — a
 * bare "45.00 per person" on a Ghanaian caterer's menu is a real bug this
 * codebase has already shipped once.
 */
export function formatMenuPrice(item: OrderingMenuItem): string {
  const symbol = item.currency || '';
  if (typeof item.price === 'number') return `${symbol}${item.price.toFixed(2)}`;
  if (typeof item.price_value === 'number' && !item.price) {
    return `${symbol}${item.price_value.toFixed(2)}`;
  }
  if (!item.price) return '';
  return `${symbol}${item.price}`;
}
